import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Ban, Flag } from "lucide-react";
import { Button, Field, Modal, Select, Textarea, toast } from "@/ui";
import { api } from "../_shims";

const REASONS = [
  { id: "spam", label: "Spam or unwanted promotion" },
  { id: "harassment", label: "Harassment or threats" },
  { id: "impersonation", label: "Pretending to be someone else" },
  { id: "inappropriate", label: "Inappropriate content" },
  { id: "other", label: "Something else" },
] as const;
type Reason = (typeof REASONS)[number]["id"];

/** Block and Report for a person on Hub. Either one keeps them from reaching you; a report also goes to us for review. */
export function BlockReport({ userId, name, conversationId, onDone }: { userId: string; name: string; conversationId?: string; onDone?: () => void }) {
  const qc = useQueryClient();
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState<Reason>("spam");
  const [details, setDetails] = useState("");
  const after = () => {
    qc.invalidateQueries({ queryKey: ["network"] });
    onDone?.();
  };
  const block = useMutation({
    mutationFn: () => api.block(userId),
    onSuccess: () => {
      toast(`${name} is blocked and can no longer message you or ask to connect.`, "success");
      after();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not block.", "danger"),
  });
  const report = useMutation({
    mutationFn: () => api.report({ userId, reason, details: details.trim() || null, conversationId: conversationId ?? null }),
    onSuccess: () => {
      setReporting(false);
      toast(`Reported. We review every report, and ${name} can no longer reach you.`, "success");
      after();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not send the report.", "danger"),
  });
  return (
    <>
      <Button size="sm" variant="ghost" icon={<Flag size={13} />} onClick={() => setReporting(true)}>
        Report
      </Button>
      <Button size="sm" variant="ghost" icon={<Ban size={13} />} loading={block.isPending} onClick={() => window.confirm(`Block ${name}? They cannot message you, ask to connect or see you in the directory. Your connection ends.`) && block.mutate()}>
        Block
      </Button>
      <Modal
        open={reporting}
        onClose={() => setReporting(false)}
        title={`Report ${name}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setReporting(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={report.isPending} onClick={() => report.mutate()}>
              Send report
            </Button>
          </>
        }
      >
        <Field label="What happened">
          <Select value={reason} onChange={(e) => setReason(e.target.value as Reason)}>
            {REASONS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Details" hint="Optional. Sending a report also blocks them.">
          <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} />
        </Field>
      </Modal>
    </>
  );
}
